/**
 * GlobalCreateFab — PD-FAB-01 (Wave 2) + PD-CHK-01 (Wave 5).
 *
 * Bottom-center circular "+" button; opens an action sheet with
 * 發布貼文, 建立行程, 發起揪團, 打卡.
 *
 * Check-in: navigates to /social/check-ins/new (PLACE SocialPost).
 * Group trip: navigates to /group-trips/picker (picker selects own PUBLISHED
 *   journey and supplies publication id before /group-trips/new).
 *
 * A11y: aria-expanded / aria-controls, dialog semantics, focus trap,
 * focus restore, Escape / scrim / toggle close. No backend API calls from FAB.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTravelText } from '../app/TravelLocaleContext';
import './global-create-fab.css';

/**
 * 在已有本頁 sticky 主 CTA 的建立／編輯流程隱藏 FAB，
 * 避免行動版遮住表單末欄與 sticky footer（Post-Epic Visual UAT）。
 */
export function shouldHideGlobalCreateFab(pathname) {
  const path = String(pathname || '');
  if (path === '/journeys/new') return true;
  if (path === '/social/posts/new') return true;
  if (path === '/social/check-ins/new') return true;
  if (path === '/group-trips/picker' || path === '/group-trips/new') return true;
  if (/^\/social\/posts\/[^/]+\/edit$/.test(path)) return true;
  return false;
}

/** SVG icons — Figma sheet uses glyph placeholders；repo 用真實 path，不用 emoji／文字字元假裝 icon。 */
function SheetActionIcon({ id }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    'aria-hidden': true,
  };
  if (id === 'post') {
    return (
      <svg {...common}>
        <rect x="5" y="4" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 9h8M8 13h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (id === 'journey') {
    return (
      <svg {...common}>
        <rect x="4" y="5" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 9h8M8 13h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (id === 'group-trip') {
    return (
      <svg {...common}>
        <circle cx="9" cy="9" r="3" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="16" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
        <path
          d="M4 18c1.2-2.2 3-3.2 5-3.2S12.8 15.8 14 18M14.5 18c.6-1.4 1.7-2.1 3.2-2.1"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="2.2" fill="currentColor" />
      <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

const SHEET_ACTIONS = [
  {
    id: 'post',
    labelKey: 'travel.create.post.label',
    descriptionKey: 'travel.create.post.description',
    to: '/social/posts/new',
    disabled: false,
  },
  {
    id: 'journey',
    labelKey: 'travel.create.journey.label',
    descriptionKey: 'travel.create.journey.description',
    to: '/journeys/new',
    disabled: false,
  },
  {
    id: 'group-trip',
    labelKey: 'travel.create.group-trip.label',
    descriptionKey: 'travel.create.group-trip.description',
    to: '/group-trips/picker',
    disabled: false,
  },
  {
    id: 'checkin',
    labelKey: 'travel.create.checkin.label',
    descriptionKey: 'travel.create.checkin.description',
    to: '/social/check-ins/new',
    disabled: false,
  },
];

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function listFocusable(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR))
    .filter((el) => el.getAttribute('aria-hidden') !== 'true');
}

/**
 * @param {{ showTrigger?: boolean }} props
 * showTrigger=false：僅掛載 sheet／事件（IA v2 primary「＋」當 trigger，避免雙 ＋）。
 */
export default function GlobalCreateFab({ showTrigger = true } = {}) {
  const { t } = useTravelText();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const touchStartY = useRef(null);
  const sheetRef = useRef(null);
  const fabRef = useRef(null);
  const openerRef = useRef(null);
  const restoreFocusRef = useRef(false);
  const sheetId = useId();
  const hideFab = shouldHideGlobalCreateFab(location.pathname);

  const close = useCallback(() => {
    restoreFocusRef.current = true;
    setOpen(false);
  }, []);
  const toggle = useCallback(() => {
    setOpen((prev) => {
      if (prev) restoreFocusRef.current = true;
      return !prev;
    });
  }, []);

  // 離開建立流程時關閉 sheet，避免殘留 scrim
  useEffect(() => {
    if (hideFab) setOpen(false);
  }, [hideFab]);

  // Focus trap + Escape while open; restore focus to FAB on close/unmount cleanup
  useEffect(() => {
    if (hideFab) return undefined;
    const fabNode = fabRef.current;
    if (!open) {
      if (restoreFocusRef.current) {
        restoreFocusRef.current = false;
        (openerRef.current || fabNode)?.focus();
        openerRef.current = null;
      }
      return undefined;
    }

    const sheet = sheetRef.current;
    const focusables = listFocusable(sheet);
    const first = focusables[0];
    first?.focus();

    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== 'Tab' || !sheet) return;
      const nodes = listFocusable(sheet);
      if (nodes.length === 0) {
        e.preventDefault();
        return;
      }
      const firstEl = nodes[0];
      const lastEl = nodes[nodes.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === firstEl || !sheet.contains(document.activeElement)) {
          e.preventDefault();
          lastEl.focus();
        }
      } else if (document.activeElement === lastEl || !sheet.contains(document.activeElement)) {
        e.preventDefault();
        firstEl.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (restoreFocusRef.current) {
        restoreFocusRef.current = false;
        (openerRef.current || fabNode)?.focus();
        openerRef.current = null;
      }
    };
  }, [open, close, hideFab]);

  useEffect(() => {
    function onOpen(event) {
      if (hideFab) return;
      openerRef.current = event.detail?.opener || null;
      setOpen(true);
    }
    window.addEventListener('travel-global-create-open', onOpen);
    return () => window.removeEventListener('travel-global-create-open', onOpen);
  }, [hideFab]);

  // Lock background scroll while sheet is open; restore on close/unmount.
  useEffect(() => {
    if (!open || hideFab) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open, hideFab]);

  // Clear swipe state on unmount
  useEffect(() => () => {
    touchStartY.current = null;
  }, []);

  if (hideFab) return null;

  function onTouchStart(e) {
    touchStartY.current = e.touches[0]?.clientY ?? null;
  }

  function onTouchEnd(e) {
    if (touchStartY.current == null) return;
    const dy = (e.changedTouches[0]?.clientY ?? touchStartY.current) - touchStartY.current;
    if (dy > 60) close();
    touchStartY.current = null;
  }

  function onTouchCancel() {
    touchStartY.current = null;
  }

  function handleAction(action) {
    if (action.disabled) return;
    close();
    if (action.to) navigate(action.to);
  }

  return (
    <>
      {/* Scrim — closes on outside click; only mounted when open (feed still scrolls when closed) */}
      {open && (
        <div
          className="global-fab__scrim"
          aria-hidden="true"
          data-testid="global-fab-scrim"
          onClick={close}
        />
      )}

      {open && (
        <div
          ref={sheetRef}
          id={sheetId}
          className="global-fab__sheet"
          role="dialog"
          aria-label={t('travel.create.title')}
          aria-modal="true"
          data-testid="global-fab-sheet"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchCancel}
        >
          <div className="global-fab__handle" aria-hidden="true" />
          <h2 className="global-fab__title">{t('travel.create.title')}</h2>
          <p className="global-fab__subtitle">{t('travel.create.subtitle')}</p>

          <ul className="global-fab__options">
            {SHEET_ACTIONS.map((action) => (
              <li key={action.id}>
                <button
                  type="button"
                  className={
                    `global-fab__option${action.disabled ? ' global-fab__option--disabled' : ''}`
                  }
                  onClick={() => handleAction(action)}
                  disabled={action.disabled}
                  aria-disabled={action.disabled || undefined}
                  data-testid={`global-fab-option-${action.id}`}
                >
                  <span className="global-fab__option-content">
                    <span className="global-fab__option-icon" aria-hidden="true">
                      <SheetActionIcon id={action.id} />
                    </span>
                    <span className="global-fab__option-text">
                      <span className="global-fab__option-label">{t(action.labelKey)}</span>
                      {action.descriptionKey ? (
                        <span className="global-fab__option-desc">{t(action.descriptionKey)}</span>
                      ) : null}
                    </span>
                  </span>
                  <span className="global-fab__option-chevron" aria-hidden="true">›</span>
                </button>
              </li>
            ))}
          </ul>

          <button
            type="button"
            className="global-fab__cancel"
            onClick={close}
            data-testid="global-fab-cancel"
          >
            {t('travel.create.cancel')}
          </button>
        </div>
      )}

      {showTrigger ? (
        <button
          ref={fabRef}
          type="button"
          className={`global-fab__button${open ? ' global-fab__button--open' : ''}`}
          aria-label={t(open ? 'travel.create.close' : 'travel.create.open')}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-controls={sheetId}
          onClick={toggle}
          data-testid="global-create-fab"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 5v14M5 12h14"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      ) : null}
    </>
  );
}
